/**
 * Генерация КП в PDF из готового HTML-шаблона.
 *
 * jsPDF и html2canvas грузятся динамически (только в момент клика «Скачать КП»),
 * чтобы не утяжелять главную страницу. Подход повторяет референс met-panel.ru:
 * html2canvas снимает скрытый шаблон в картинку, jsPDF раскладывает её по A4.
 */

/**
 * Снять DOM-элемент в PDF (A4, портрет) и инициировать скачивание.
 * Элемент должен быть отрисован (не display:none) — допустимо вынести его за экран.
 */
async function createElementPdf(el: HTMLElement) {
  const [{ jsPDF }, html2canvasMod] = await Promise.all([
    import('jspdf'),
    import('html2canvas'),
  ]);
  const html2canvas = html2canvasMod.default;

  const canvas = await html2canvas(el, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false,
  });

  const img = canvas.toDataURL('image/jpeg', 0.95);
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();

  // Вписываем картинку по ширине страницы, сохраняя пропорции.
  let renderW = pageW;
  let renderH = (canvas.height * renderW) / canvas.width;

  // Если по высоте не помещается на одну страницу — масштабируем по высоте.
  if (renderH > pageH) {
    renderH = pageH;
    renderW = (canvas.width * renderH) / canvas.height;
  }

  const x = (pageW - renderW) / 2;
  const y = renderH < pageH ? (pageH - renderH) / 2 : 0;

  pdf.addImage(img, 'JPEG', x, y, renderW, renderH);
  return pdf;
}

export async function exportElementToPdf(el: HTMLElement, filename: string): Promise<void> {
  const pdf = await createElementPdf(el);
  pdf.save(filename);
}

export async function createElementPdfBlob(el: HTMLElement): Promise<Blob> {
  const pdf = await createElementPdf(el);
  return pdf.output('blob');
}

/** The same bytes are uploaded to CRM and downloaded by the customer. */
export function pdfBlobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = () => reject(new Error('Не удалось подготовить PDF для отправки.'));
    reader.readAsDataURL(blob);
  });
}

export function pdfBlobFromBase64(content: string): Blob {
  const bytes = Uint8Array.from(atob(content), (char) => char.charCodeAt(0));
  return new Blob([bytes], { type: 'application/pdf' });
}

export function downloadPdfBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url; link.download = filename;
  document.body.appendChild(link); link.click(); link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
